import { useState, useEffect, useRef, useCallback } from 'react';
import gsap from 'gsap';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router';
import { trpc } from '@/providers/trpc';
import PasswordStrength from '@/components/PasswordStrength';
import PasswordReset from '@/components/PasswordReset';
import { Chrome, Mail, Apple } from 'lucide-react';
import { trackSignupStart, trackSignupComplete, trackFeatureUse } from '@/lib/analytics';
import { useFocusTrap } from '@/hooks/useFocusTrap';


interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialMode?: 'login' | 'signup';
}

export default function LoginModal({ isOpen, onClose, initialMode = 'login' }: LoginModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState<'login' | 'signup'>(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [authMethod, setAuthMethod] = useState<'password' | 'magic'>('password');

  const [unverifiedEmail, setUnverifiedEmail] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [signupMessage, setSignupMessage] = useState('');
  const trapRef = useFocusTrap(isOpen, { onEscape: onClose, returnFocusOnDeactivate: true });

  const googleUrlQuery = trpc.auth.googleOAuthUrl.useQuery(undefined, { enabled: false });
  const appleUrlQuery = trpc.auth.appleOAuthUrl.useQuery(undefined, { enabled: false });
  const resendVerification = trpc.auth.resendVerification.useMutation({
    onSuccess: () => setResendSuccess(true),
    onError: (err) => setErrors({ email: err.message }),
  });

  useEffect(() => {
    const overlay = overlayRef.current;
    const modal = modalRef.current;
    if (!overlay || !modal) return;

    if (isOpen) {
      document.body.style.overflow = 'hidden';
      gsap.to(overlay, { opacity: 1, duration: 0.3, ease: 'power2.out' });
      gsap.fromTo(
        modal,
        { y: 40, opacity: 0, scale: 0.97 },
        { y: 0, opacity: 1, scale: 1, duration: 0.4, ease: 'power3.out', delay: 0.1 }
      );
    } else {
      document.body.style.overflow = '';
      gsap.to(modal, { y: 20, opacity: 0, scale: 0.97, duration: 0.25, ease: 'power2.in' });
      gsap.to(overlay, { opacity: 0, duration: 0.3, ease: 'power2.in', delay: 0.05 });
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const validate = () => {
    const e: Record<string, string> = {};
    if (!email.trim()) e.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = 'Enter a valid email address';
    if (!password.trim()) e.password = 'Password is required';
    else if (mode === 'signup' && password.length < 8) e.password = 'Minimum 8 characters required';
    else if (mode === 'login' && password.length < 1) e.password = 'Password is required';
    // Name is optional for signup — no validation required
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const clearFieldError = (field: string) => {
    setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    setUnverifiedEmail(false);
    setResendSuccess(false);
    setSignupSuccess(false);
    setErrors({});

    try {
      if (mode === 'signup') {
        const result = await register(email, password, name);
        console.log('[Registration] Result:', result.success ? 'success' : 'error', result.error || '');
        if (result.success) {
          // P0 FIX: Auto-redirect to dashboard after successful registration
          onClose();
          navigate('/dashboard');
        } else {
          setErrors({ general: result.error || 'Registration failed. Please try again.' });
        }
      } else {
        const result = await login(email, password);
        if (result.success) {
          onClose();
          navigate('/dashboard');
        } else if (result.error?.toLowerCase().includes('not verified') || result.error?.toLowerCase().includes('verification')) {
          setUnverifiedEmail(true);
          setErrors({ general: result.error });
        } else {
          setErrors({ general: result.error || 'Login failed. Please check your credentials.' });
        }
      }
    } catch (err: any) {
      console.error('[Auth] Unexpected error:', err);
      setErrors({ general: 'Something went wrong. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setErrors({});
    setEmail('');
    setPassword('');
    setName('');
  };

  const switchMode = (m: 'login' | 'signup') => {
    if (m === 'signup') trackSignupStart('email');
    setMode(m);
    resetForm();
  };

  const handleGoogleSignIn = async () => {
    try {
      const result = await googleUrlQuery.refetch();
      if (result.data?.url) {
        window.location.href = result.data.url;
      }
    } catch {
      setErrors({ general: 'Google Sign-In is not configured yet.' });
    }
  };

  const handleAppleSignIn = async () => {
    try {
      const result = await appleUrlQuery.refetch();
      if (result.data?.url) {
        window.location.href = result.data.url;
      }
    } catch {
      setErrors({ general: 'Apple Sign-In is not configured yet.' });
    }
  };

  if (!isOpen && !showReset) return null;

  return (
    <>
      <PasswordReset isOpen={showReset} onClose={() => { setShowReset(false); }} />
      {isOpen && <div
        ref={overlayRef}
        className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center px-4 py-4 sm:py-0"
        style={{ background: 'rgba(26, 35, 50, 0.6)', backdropFilter: 'blur(8px)', opacity: 0 }}
        onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
      >
        <div
          ref={modalRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="login-modal-title"
          className="relative w-full max-w-[440px] max-h-[92dvh] sm:max-h-none overflow-y-auto sm:overflow-hidden opacity-0"
          style={{ background: '#101D29', borderRadius: 16, boxShadow: '0 24px 80px rgba(0,0,0,0.25)' }}
        >
        {/* Header */}
        <div className="px-8 pt-8 pb-2">
          <h1 id="login-modal-title" className="font-display font-semibold" style={{ fontSize: 28, color: '#F7F8F4', lineHeight: 1.2 }}>
            {mode === 'login' ? 'Sign In' : 'Get Started'}
          </h1>
          <p className="font-body mt-1" style={{ fontSize: 14, color: '#A8B8AE' }}>
            {mode === 'login'
              ? 'Access your Parcel Lead Pro account.'
              : 'Create your account and start exploring parcels.'}
          </p>
        </div>

        {/* Mode Toggle */}
        <div className="mx-8 mb-4 mt-4 flex rounded-full p-1" style={{ background: '#132034' }} role="tablist" aria-label="Authentication mode">
          <button
            onClick={() => switchMode('login')}
            className="flex-1 rounded-full py-2 text-xs font-medium transition-all"
            style={{ background: mode === 'login' ? '#7CFF6B' : 'transparent', color: mode === 'login' ? '#07110D' : '#F7F8F4', boxShadow: mode === 'login' ? '0 1px 4px rgba(0,0,0,0.08)' : 'none' }}
            role="tab"
            aria-selected={mode === 'login'}
            aria-controls="auth-form-panel"
          >
            Sign In
          </button>
          <button
            onClick={() => switchMode('signup')}
            className="flex-1 rounded-full py-2 text-xs font-medium transition-all"
            style={{ background: mode === 'signup' ? '#7CFF6B' : 'transparent', color: mode === 'signup' ? '#07110D' : '#F7F8F4', boxShadow: mode === 'signup' ? '0 1px 4px rgba(0,0,0,0.08)' : 'none' }}
            role="tab"
            aria-selected={mode === 'signup'}
            aria-controls="auth-form-panel"
          >
            Get Started
          </button>
        </div>

        {/* SSO Options */}
        <div className="px-8 pb-4">
          {/* Google Sign In */}
          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={submitting}
            className="flex w-full items-center justify-center gap-2.5 rounded-full py-3 text-sm font-medium transition-all hover:opacity-90 disabled:opacity-60"
            style={{ background: '#132034', border: '1px solid rgba(247,248,244,0.12)', color: '#F7F8F4' }}
          >
            <Chrome size={18} /> Continue with Google
          </button>

          {/* Apple Sign In */}
          <button
            type="button"
            onClick={handleAppleSignIn}
            disabled={submitting}
            className="mt-2 flex w-full items-center justify-center gap-2.5 rounded-full py-3 text-sm font-medium transition-all hover:opacity-90 disabled:opacity-60"
            style={{ background: '#132034', border: '1px solid rgba(247,248,244,0.12)', color: '#F7F8F4' }}
          >
            <Apple size={18} /> Continue with Apple
          </button>

          {/* Divider */}
          <div className="my-4 flex items-center gap-3">
            <div className="h-px flex-1" style={{ background: 'rgba(255,255,255,0.1)' }} />
            <span className="text-[11px] font-medium" style={{ color: '#A8B8AE' }}>OR</span>
            <div className="h-px flex-1" style={{ background: 'rgba(255,255,255,0.1)' }} />
          </div>

          {/* Auth method toggle — Magic Link hidden (P0: Resend not configured, silent failure) */}
          {mode === 'login' && (
            <div className="mb-4 flex gap-2">
              <button
                type="button"
                onClick={() => setAuthMethod('password')}
                className="flex items-center gap-1.5 rounded-full px-4 py-2 text-[11px] font-medium transition-all"
                style={{
                  background: '#132034',
                  color: '#F7F8F4',
                  border: '1px solid rgba(255,255,255,0.1)',
                }}
              >
                <Mail size={12} /> Password
              </button>
            </div>
          )}
        </div>

        {/* Form */}
        <form id="auth-form-panel" role="tabpanel" onSubmit={handleSubmit} className="px-8 pb-8" aria-live="polite" aria-atomic="true">
          {mode === 'signup' && (
            <div className="mb-4">
              <label className="label-accent mb-2 block" style={{ color: '#A8B8AE', fontSize: 11 }}>
                Full Name <span style={{ color: '#A8B8AE', fontSize: 10 }}>(optional)</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => { setName(e.target.value); clearFieldError('name'); }}
                placeholder="John Smith"
                className="font-body w-full rounded-lg px-4 py-3 text-sm outline-none transition-all focus:ring-2 focus:ring-[#7CFF6B]"
                style={{ border: `1px solid ${errors.name ? '#FF445F' : 'rgba(247,248,244,0.12)'}`, background: '#132034', color: '#F7F8F4' }}
                aria-label="Full name"
                aria-invalid={!!errors.name}
              />
              {errors.name && <p id="name-error" className="mt-1 font-body text-xs" style={{ color: '#FF445F' }}>{errors.name}</p>}
            </div>
          )}

          <div className="mb-4">
            <label className="label-accent mb-2 block" style={{ color: '#A8B8AE', fontSize: 11 }}>
              Email <span style={{ color: '#7CFF6B' }}>*</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); clearFieldError('email'); }}
              placeholder="you@company.com"
              className="font-body w-full rounded-lg px-4 py-3 text-sm outline-none transition-all focus:ring-2 focus:ring-[#7CFF6B]"
              style={{ border: `1px solid ${errors.email ? '#FF445F' : 'rgba(255,255,255,0.1)'}`, background: '#132034', color: '#F7F8F4' }}
              aria-label="Email address"
              aria-invalid={!!errors.email}
              autoComplete="email"
            />
            {errors.email && <p id="email-error" className="mt-1 font-body text-xs" style={{ color: '#FF445F' }}>{errors.email}</p>}
          </div>

          {/* Password field */}
          {(mode === 'signup' || mode === 'login') && (
            <div className="mb-4">
              <label className="label-accent mb-2 block" style={{ color: '#A8B8AE', fontSize: 11 }}>
                Password <span style={{ color: '#7CFF6B' }}>*</span>
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); clearFieldError('password'); }}
                placeholder={mode === 'signup' ? 'Min 8 characters' : '••••••'}
                className="font-body w-full rounded-lg px-4 py-3 text-sm outline-none transition-all focus:ring-2 focus:ring-[#7CFF6B]"
                style={{ border: `1px solid ${errors.password ? '#FF445F' : 'rgba(255,255,255,0.1)'}`, background: '#132034', color: '#F7F8F4' }}
                aria-label="Password"
                aria-invalid={!!errors.password}
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                aria-describedby={errors.password ? "password-error" : undefined}
              />
              {mode === 'signup' && <PasswordStrength password={password} />}
              {errors.password && <p id="password-error" className="mt-1 font-body text-xs" style={{ color: '#FF445F' }}>{errors.password}</p>}
              {mode === 'login' && (
                <button
                  type="button"
                  onClick={() => { navigate('/forgot-password'); onClose(); }}
                  className="mt-2 font-body text-xs transition-colors hover:text-[#7CFF6B]"
                  style={{ color: '#A8B8AE', background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  Forgot password?
                </button>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="flex w-full items-center justify-center gap-2 rounded-full py-3.5 text-sm font-medium text-[#07110D] transition-all hover:opacity-90 disabled:opacity-60"
            style={{ background: '#7CFF6B' }}
          >
            {submitting ? (
              <>
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#07110D] border-t-transparent" />
                {mode === 'login' ? 'Signing in...' : 'Creating account...'}
              </>
            ) : (
              mode === 'login' ? 'Sign In' : 'Create Account'
            )}
          </button>

          {signupSuccess && (
            <div className="mt-4 rounded-xl p-4" style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)' }}>
              <p className="text-sm font-medium" style={{ color: '#22C55E' }}>Account created!</p>
              <p className="mt-1 text-xs" style={{ color: '#A8B8AE' }}>{signupMessage}</p>
            </div>
          )}

          {unverifiedEmail && (
            <div className="mt-4 rounded-xl p-4" style={{ background: 'rgba(255,68,95,0.08)', border: '1px solid rgba(255,68,95,0.2)' }}>
              <p className="text-sm font-medium" style={{ color: '#FF445F' }}>Email not verified</p>
              <p className="mt-1 text-xs" style={{ color: '#A8B8AE' }}>Please verify your email before logging in.</p>
              {!resendSuccess ? (
                <button
                  type="button"
                  onClick={() => {
                    resendVerification.mutate({ email });
                  }}
                  disabled={resendVerification.isPending}
                  className="mt-3 rounded-full px-4 py-2 text-xs font-medium transition-all hover:opacity-90 disabled:opacity-60"
                  style={{ background: '#7CFF6B', color: '#07110D' }}
                >
                  {resendVerification.isPending ? 'Sending...' : 'Resend Verification Email'}
                </button>
              ) : (
                <p className="mt-2 text-xs font-medium" style={{ color: '#22C55E' }}>Verification email sent! Check your inbox.</p>
              )}
            </div>
          )}

          {errors.general && <p className="mt-3 text-center font-body text-xs" style={{ color: '#FF445F' }}>{errors.general}</p>}
        </form>

        {/* Close */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:bg-white/10"
          style={{ color: '#A8B8AE' }}
          aria-label="Close"
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M3 3L15 15M15 3L3 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </div>
    </div>}
    </>
  );
}
